import { Button } from "@/components/ui/baseUi/button"
import { Text } from "@/components/ui/baseUi/text"
import { cn } from "@/modules/lib/utils"
import {
  type ExerciseFormikType,
  type ExerciseInputKey,
} from "@/modules/sessions/sessionForm/blocks/exerciseBlock/exerciseFormikTypes"
import { HUChronoInput } from "@/modules/ui/components/huChronoInput"
import { type SimpleFormik } from "@/typings/formik"
import { PlusIcon } from "@images/svgIcons/plusIcon"
import { TrashIcon } from "@images/svgIcons/trashIcon"
import { ChronoType } from "@modules/chrono/chronoTypes"
import { GetChronoDefaults, getTrainingChronoTitle } from "@modules/chrono/chronoUtils"
import { word } from "@modules/core/utils/i18n"
import { getFormErrorMessage } from "@modules/formik/formikUtils"
import { useEffect } from "react"

export type AmrapInfos = {
  trainingTime: number
  restTime: number
}

const amrapInit: AmrapInfos = {
  trainingTime: 600,
  restTime: 0,
}

type ChronoSettingsAMRAPFormProps = {
  formik: SimpleFormik<ExerciseFormikType, ExerciseInputKey>
  className?: string
} & React.ComponentProps<"div">

export function ChronoSettingsAMRAPForm({ formik, className, ...props }: ChronoSettingsAMRAPFormProps) {
  const getFieldProps = (name: string) => formik.getFieldProps(name as ExerciseInputKey)

  useEffect(() => {
    if (!formik.values.chronoAmrap) {
      void formik.setFieldValue("chronoAmrap", GetChronoDefaults(ChronoType.AMRAP))
    }
  }, [])

  const addSet = () => {
    if (formik.values.chronoAmrap) {
      void formik.setFieldValue("chronoAmrap.trainingTime", [
        ...formik.values.chronoAmrap.trainingTime,
        amrapInit.trainingTime,
      ])
      void formik.setFieldValue("chronoAmrap.restTime", [...formik.values.chronoAmrap.restTime, amrapInit.restTime])
      void formik.setFieldValue("chronoAmrap.numberOfSets", formik.values.chronoAmrap.numberOfSets + 1)
    }
  }

  const deleteSet = (index: number) => {
    if (formik.values.chronoAmrap) {
      void formik.setFieldValue(
        "chronoAmrap.trainingTime",
        formik.values.chronoAmrap.trainingTime.filter((_, i) => i !== index),
      )
      void formik.setFieldValue(
        "chronoAmrap.restTime",
        formik.values.chronoAmrap.restTime.filter((_, i) => i !== index),
      )
      void formik.setFieldValue("chronoAmrap.numberOfSets", formik.values.chronoAmrap.numberOfSets - 1)
    }
  }

  return (
    <div className={cn("flex flex-col gap-4", className)} {...props}>
      <HUChronoInput
        name="chronoAmrap.trainingTime[0]"
        label={word("chrono.settings.label.training_time")}
        getFieldProps={getFieldProps}
        setFieldValue={formik.setFieldValue}
        error={getFormErrorMessage("chronoAmrap.trainingTime[0]", formik, { showAlways: true })}
      />

      <div className="flex flex-col gap-2">
        {formik.values.chronoAmrap &&
          Array.from({ length: formik.values.chronoAmrap.numberOfSets - 1 }, (_, i) => i + 1).map((amarapIndex) => (
            <div className="flex flex-col gap-2" key={amarapIndex}>
              <div className="flex w-full items-center justify-between">
                <Text fontStyle="BL">{`${getTrainingChronoTitle(ChronoType.AMRAP)} ${(amarapIndex + 1).toString()}`}</Text>

                <Button variant="ghost" size="iconMd" onClick={() => deleteSet(amarapIndex)}>
                  <TrashIcon color="currentColor" className="text-textDefault-body" width={22} height={22} />
                </Button>
              </div>

              <div className="flex flex-col gap-2">
                <HUChronoInput
                  name={`chronoAmrap.restTime[${amarapIndex.toString()}]`}
                  label={word("chrono.settings.label.rest_time")}
                  getFieldProps={getFieldProps}
                  setFieldValue={formik.setFieldValue}
                  error={getFormErrorMessage(`chronoAmrap.restTime[${amarapIndex.toString()}]`, formik, {
                    showAlways: true,
                  })}
                />

                <HUChronoInput
                  name={`chronoAmrap.trainingTime[${amarapIndex.toString()}]`}
                  label={word("chrono.settings.label.training_time")}
                  getFieldProps={getFieldProps}
                  setFieldValue={formik.setFieldValue}
                  error={getFormErrorMessage(`chronoAmrap.trainingTime[${amarapIndex.toString()}]`, formik, {
                    showAlways: true,
                  })}
                />
              </div>
            </div>
          ))}
      </div>

      <Button variant="outline" onClick={addSet} className="shrink-0 self-center">
        <PlusIcon color="currentColor" className="text-textDefault-body" />
        {word("chrono.settings.label.add_set")}
      </Button>
    </div>
  )
}
